<template>
    <div class="na-post-detail-poll" v-if="!isLoading">
        <h2 v-if="isShowTitle" class="title is-4">{{ dataSupplemental.title }}</h2>
        
        <!-- Show results if user has voted or is not authenticated -->
        <div v-if="!isAuthenticated || hasVoted" class="poll-results">
            <h2 v-if="getTotalVotersAllQuestions() > 0" class="title is-4">{{ getTotalVotersAllQuestions() }} Voters</h2>
            <div v-for="(question, index) in dataSupplemental.questions" :key="index" class="question-results mb-5">
                <h3 v-if="question.text && ((question.type === 'free_text' && isShowTextResponses) || question.type !== 'free_text')" class="subtitle is-5 mb-2">{{ question.text }}</h3>
                
                <!-- Multiple Choice Results -->
                <div v-if="question.type === 'multiple_choice'" class="choices-results">
                    <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="choice-result mb-2">
                        <div class="level is-mobile mb-0">
                            <div class="level-left">
                                <div class="level-item">
                                    <span>{{ choice }}</span>
                                </div>
                            </div>
                            <div class="level-right">
                                <div class="level-item">
                                    <span class="has-text-weight-semibold">{{ getChoicePercentage(question, choiceIndex) }}%</span>
                                    <span class="has-text-grey ml-2">({{ getChoiceVotes(question, choiceIndex) }})</span>
                                </div>
                            </div>
                        </div>
                        <progress 
                            class="progress is-primary mt-0" 
                            :value="getChoicePercentage(question, choiceIndex)" 
                            max="100"
                        ></progress>
                    </div>
                    <!-- Other Option Results -->
                    <div v-if="question.hasOtherOption" class="choice-result mb-2">
                        <div class="level is-mobile mb-0">
                            <div class="level-left">
                                <div class="level-item">
                                    <span>Other</span>
                                </div>
                            </div>
                            <div class="level-right">
                                <div class="level-item">
                                    <span class="has-text-weight-semibold">{{ getOtherPercentage(question) }}%</span>
                                    <span class="has-text-grey ml-2">({{ getOtherVotes(question) }})</span>
                                </div>
                            </div>
                        </div>
                        <progress 
                            class="progress is-primary mt-0" 
                            :value="getOtherPercentage(question)" 
                            max="100"
                        ></progress>
                        
                        <!-- Show approved "Other" text responses -->
                        <div v-if="getTextResponses(question).length > 0" class="mt-3 ml-4">
                            <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
                                <p class="has-text-weight-semibold is-size-7 mb-0">
                                    Other Responses ({{ getTextResponses(question).length }})
                                </p>
                                <button 
                                    class="button is-small is-ghost"
                                    @click="toggleTextResponses(question.id)"
                                >
                                    <span class="icon is-small">
                                        <fa-icon :icon="isTextResponsesExpanded(question.id) ? 'chevron-up' : 'chevron-down'" />
                                    </span>
                                    <span class="is-size-7">{{ isTextResponsesExpanded(question.id) ? 'Hide' : 'Show' }}</span>
                                </button>
                            </div>
                            <div v-if="isTextResponsesExpanded(question.id)">
                                <div v-for="(response, responseIndex) in getTextResponses(question)" :key="responseIndex" class="text-response mb-2">
                                    <div class="box is-small py-2 px-3">
                                        <div class="is-flex is-align-items-start is-justify-content-space-between">
                                            <p class="is-size-7 mb-0">"{{ response.text || response }}"</p>
                                            <span v-if="canAccessAdministration && typeof response === 'object' && !response.approved" class="tag is-warning is-light is-small ml-2" style="flex-shrink: 0;">
                                                <span class="icon is-small">
                                                    <fa-icon icon="eye-slash" />
                                                </span>
                                                <span>Admin Only</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Text Response Results -->
                <div v-else-if="question.type === 'free_text'" class="text-responses">
                    <div v-if="getTextResponses(question).length > 0">
                        <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
                            <p class="has-text-weight-semibold mb-0">
                                Other Responses ({{ getTextResponses(question).length }})
                            </p>
                            <button 
                                class="button is-small is-ghost"
                                @click="toggleTextResponses(question.id)"
                            >
                                <span class="icon is-small">
                                    <fa-icon :icon="isTextResponsesExpanded(question.id) ? 'chevron-up' : 'chevron-down'" />
                                </span>
                                <span>{{ isTextResponsesExpanded(question.id) ? 'Hide' : 'Show' }}</span>
                            </button>
                        </div>
                        <div v-if="isTextResponsesExpanded(question.id)">
                            <div v-for="(response, responseIndex) in getTextResponses(question)" :key="responseIndex" class="text-response mb-2">
                                <div class="box">
                                    <div class="is-flex is-align-items-start is-justify-content-space-between">
                                        <p class="mb-0">"{{ response.text || response }}"</p>
                                        <span v-if="canAccessAdministration && typeof response === 'object' && !response.approved" class="tag is-warning is-light is-small ml-2" style="flex-shrink: 0;">
                                            <span class="icon is-small">
                                                <fa-icon icon="eye-slash" />
                                            </span>
                                            <span>Admin Only</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="has-text-grey is-italic">
                        <p>No approved responses yet.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Show voting form if user is authenticated and hasn't voted -->
        <div v-else class="poll-voting">
            <Error :errors="errors" />
            <form @submit.prevent="submitVote">
                <div v-for="(question, index) in dataSupplemental.questions" :key="index" class="question-voting mb-5">
                    <h3 class="subtitle is-5 mb-2">{{ question.text }}</h3>
                    
                    <!-- Multiple Choice Input -->
                    <div v-if="question.type === 'multiple_choice'" class="choices-input">
                        <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="control mb-2">
                            <label class="radio">
                                <input 
                                    type="radio" 
                                    :name="`question-${index}`" 
                                    :value="choiceIndex"
                                    v-model="responses[index]"
                                    required
                                >
                                {{ choice }}
                            </label>
                        </div>
                        <!-- Other Option Input -->
                        <div v-if="question.hasOtherOption" class="control mb-2">
                            <label class="radio">
                                <input 
                                    type="radio" 
                                    :name="`question-${index}`" 
                                    value="other"
                                    v-model="responses[index]"
                                    required
                                >
                                Other
                            </label>
                            <div v-if="responses[index] === 'other'" class="ml-4 mt-2">
                                <textarea 
                                    class="textarea" 
                                    :placeholder="'Please specify...'"
                                    v-model="otherResponses[index]"
                                    required
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <!-- Text Response Input -->
                    <div v-else-if="question.type === 'free_text'" class="text-input">
                        <div class="field">
                            <div class="control">
                                <textarea 
                                    class="textarea" 
                                    :placeholder="'Enter your response'"
                                    v-model="responses[index]"
                                    required
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <div class="control">
                        <button 
                            type="submit" 
                            class="button is-primary"
                            :class="{ 'is-loading': isSubmitting }"
                            :disabled="isSubmitting"
                        >
                            Submit Vote
                        </button>
                    </div>
                </div>
            </form>
        </div>

        <!-- Poll Status -->
        <div class="poll-status mt-4">
            <!-- Countdown Timer -->
            <div v-if="dataSupplemental.ends_at" class="countdown-timer mb-3">
                <div class="notification" :class="countdownClass">
                    <div class="countdown-display">
                        <i class="fas fa-clock mr-2"></i>
                        <span class="countdown-text">{{ countdownText }}</span>
                    </div>
                    <div class="countdown-details has-text-grey-dark is-size-7">
                        <span v-if="!timeRemaining?.expired">Poll ends: {{ formatDate(dataSupplemental.ends_at) }}</span>
                    </div>
                </div>
            </div>
            
            <p v-if="!isAuthenticated" class="has-text-info">
                Please log in to vote in this poll
            </p>
            <p v-else-if="hasVoted" class="has-text-success">
                You have already voted in this poll
                <span v-if="isPollOpen()" class="ml-2">
                    (<a href="#" @click.prevent="unvote" class="has-text-danger" :class="{ 'is-loading': isLoading }">unvote</a>)
                </span>
            </p>
        </div>
    </div>
</template>

<script>
import { formatDateTime } from '@/helpers/date.js';
import { getPollStatus, vote, unvote } from '@/services/poll.js';
import { getTimeRemaining, formatTimeRemaining } from '@/helpers/countdown.js';
import Error from '@/components/Common/Error.vue';

export default {
    name: 'PollViewer',

    components: {
        Error
    },

    props: {
        post: {
            type: Object,
            required: true
        },
        dataSupplemental: {
            type: Object,
            required: true
        },
        webviewUserId: {
            type: Number,
            default: 0
        },
        webviewIdentifier: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            responses: [],
            otherResponses: [],
            hasVoted: false,
            isSubmitting: false,
            pollResults: null,
            isLoading: false,
            errors: [],
            isShowTitle: false,
            countdownInterval: null,
            timeRemaining: null,
            isShowTextResponses: false,  // Show text response results if true
            totalVoters: 0,
            expandedTextResponses: {},  // Track which questions have their text responses expanded (keyed by question.id)
            isAuthenticated: false,
        };
    },

    computed: {
        countdownText() {
            if (!this.timeRemaining) return '';
            return formatTimeRemaining(this.timeRemaining);
        },
        
        countdownClass() {
            if (!this.timeRemaining) return 'is-info';
            
            if (this.timeRemaining.expired) {
                return 'is-danger';
            } else if (this.timeRemaining.days === 0 && this.timeRemaining.hours < 2) {
                return 'is-warning';
            } else {
                return 'is-info';
            }
        }
    },

    methods: {
        formatDate(date) {
            // What is the moment dateformat for June 1, 2025 at 12:00 PM?
            return formatDateTime(date, 'MMMM D, h:mm a');
        },

        toggleTextResponses(questionId) {
            this.$set(this.expandedTextResponses, questionId, !this.expandedTextResponses[questionId]);
        },

        isTextResponsesExpanded(questionId) {
            return this.expandedTextResponses[questionId] || false;
        },

        async checkVoteStatus() {
            this.errors = [];
            this.isLoading = true;

            try {
                const response = await getPollStatus({
                    data: {
                        post_id: this.post.id,
                        webview_post_id: this.post.id,
                        webview_user_id: this.webviewUserId || null,
                        webview_identifier: this.webviewIdentifier || null
                    }
                });
                this.isAuthenticated = !!response.isAuthenticated;
                this.hasVoted = response.hasVoted;
                this.pollResults = response.results;
                this.totalVoters = response.totalVoters;
            } catch (error) {
                console.error('Error checking vote status:', error);
                this.errors = [ 'Failed to check vote status. Please try again.' ];
            } finally {
                this.isLoading = false;
            }
        },

        getChoiceVotes(question, choiceIndex) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.choices[choiceIndex] || 0;
        },

        getChoicePercentage(question, choiceIndex) {
            const votes = this.getChoiceVotes(question, choiceIndex);
            const totalVotes = this.getTotalVotes(question);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },

        getTotalVotes(question) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.totalVotes || 0;
        },

        getTotalVotersAllQuestions() {
            return this.totalVoters;
        },

        getTextResponses(question) {
            if (!this.pollResults) return [];
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return [];
            
            const responses = questionResults.otherResponses || [];
            
            // For non-admin users, only show approved responses
            if (!this.canAccessAdministration) {
                return responses.filter(response => {
                    // If response is a string (old format), hide it
                    if (typeof response === 'string') return false;
                    // Otherwise check if it's approved
                    return response.approved === true;
                });
            }
            
            // Admin sees all responses (approved and unapproved)
            return responses;
        },

        getOtherVotes(question) {
            if (!this.pollResults) return 0;
            const questionResults = this.pollResults[question.id];
            if (!questionResults) return 0;
            return questionResults.otherVotes || 0;
        },

        getOtherPercentage(question) {
            const votes = this.getOtherVotes(question);
            const totalVotes = this.getTotalVotes(question);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },

        async submitVote() {
            if (!this.isAuthenticated || this.hasVoted) return;
            this.errors = [];

            this.isLoading = true;
            
            this.isSubmitting = true;
            try {
                const response = await vote({
                    data: {
                        post_id: this.post.id,
                        webview_post_id: this.post.id,
                        responses: this.responses,
                        otherResponses: this.otherResponses,
                        webview_user_id: this.webviewUserId || null,
                        webview_identifier: this.webviewIdentifier || null
                    }
                });
                
                this.hasVoted = true;
                this.pollResults = response.results;
            } catch (error) {
                console.error('Error submitting vote:', error);
                if (error.response && error.response.status === 401) {
                    this.errors = ['Please log in to vote in this poll'];
                } else {
                    this.errors = ['Failed to submit vote. Please try again.'];
                }
            } finally {
                this.isSubmitting = false;
                this.isLoading = false;
            }
        },

        isPollOpen() {
            if (!this.dataSupplemental.ends_at) return true;
            return new Date(this.dataSupplemental.ends_at) > new Date();
        },

        async unvote() {
            if (!this.isAuthenticated || !this.hasVoted || !this.isPollOpen()) return;
            this.errors = [];

            this.isLoading = true;
            
            try {
                const response = await unvote({
                    data: {
                        post_id: this.post.id,
                        webview_post_id: this.post.id,
                        webview_user_id: this.webviewUserId || null,
                        webview_identifier: this.webviewIdentifier || null
                    }
                });
                
                this.hasVoted = false;
                this.pollResults = response.results;
                this.responses = [];
                this.otherResponses = [];
            } catch (error) {
                console.error('Error unvoting:', error);
                if (error.response && error.response.status === 401) {
                    this.errors = ['Please log in to unvote in this poll'];
                } else if (error.response && error.response.status === 400) {
                    this.errors = [error.response.data.error || 'Failed to unvote. Please try again.'];
                } else {
                    this.errors = ['Failed to unvote. Please try again.'];
                }
            } finally {
                this.isLoading = false;
            }
        },

        startCountdown() {
            if (!this.dataSupplemental.ends_at) return;
            
            this.updateCountdown();
            this.countdownInterval = setInterval(() => {
                this.updateCountdown();
            }, 1000);
        },

        stopCountdown() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
            }
        },

        updateCountdown() {
            if (!this.dataSupplemental.ends_at) return;
            
            this.timeRemaining = getTimeRemaining(this.dataSupplemental.ends_at);
            
            // Stop countdown if poll has expired
            if (this.timeRemaining.expired) {
                this.stopCountdown();
            }
        }
    },

    mounted() {
        this.checkVoteStatus();
        this.startCountdown();
    },

    beforeDestroy() {
        this.stopCountdown();
    }
};
</script>

<style scoped>
.na-post-detail-poll {
    /* max-width: 800px; */
    margin: 0 auto;
}

/*
.question-results,
.question-voting {
    background: #f5f5f5;
    padding: 1.5rem;
    border-radius: 4px;
}
*/

.choice-result {
    margin-bottom: 1rem;
}

.progress {
    margin-top: 0.5rem;
}

.text-response {
    margin-bottom: 1rem;
}

.text-response .box {
    background: white;
    padding: 1rem;
}

.poll-status {
    text-align: center;
    font-size: 0.9rem;
}

.countdown-timer {
    text-align: center;
}

.countdown-display {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
}

.countdown-text {
    font-family: 'Courier New', monospace;
}

.countdown-details {
    margin-top: 0.5rem;
    opacity: 0.8;
}

.notification.is-info {
    background-color: #e8f4fd;
    color: #1e40af;
}

.notification.is-warning {
    background-color: #fef3c7;
    color: #92400e;
}

.notification.is-danger {
    background-color: #fee2e2;
    color: #dc2626;
}
</style>
